import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";
import { ROLE_LABELS } from "@/lib/statusStyles";
import ImportButton from "@/components/ImportButton";
import { ExportImportButtons } from "@/components/ExportImportButtons";

const NAMED_TABS = [
  { key: "brands", label: "Brands" },
  { key: "dealers", label: "Dealers" },
  { key: "provinces", label: "Provinces" },
  { key: "warranty-types", label: "Warranty Types" },
  { key: "service-types", label: "Service Types" },
  { key: "complaint-natures", label: "Complaint Natures" },
];

function NamedMaster({ kind }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const isDealers = kind === "dealers";
  const blank = isDealers
    ? { name: "", address: "", area: "", district: "", province: "",
        phone: "", contact_person: "", email: "", remarks: "", active: true }
    : { name: "", remarks: "", active: true };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => api.get(`/masters/${kind}`).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing) await api.put(`/masters/${kind}/${editing.id}`, form);
      else await api.post(`/masters/${kind}`, form);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(blank); load();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/masters/${kind}/${id}`); load(); };

  const filtered = !isDealers || !search ? items : items.filter((it) =>
    [it.name, it.address, it.area, it.district, it.province, it.phone, it.contact_person]
      .some((v) => (v || "").toLowerCase().includes(search.toLowerCase())));

  return (
    <Card><CardContent className="p-0">
      <div className="flex items-center justify-between p-3 border-b gap-3 flex-wrap">
        <div className="text-sm text-slate-600 whitespace-nowrap">{items.length} records</div>
        {isDealers && (
          <Input className="max-w-xs" placeholder="Search name/address/area/district/phone…"
                 value={search} onChange={(e) => setSearch(e.target.value)} data-testid="dealers-search-input" />
        )}
        <div className="flex gap-2">
          {isDealers && <ExportImportButtons kind="dealers" label="Dealers" onDone={load} />}
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); } }}>
            <DialogTrigger asChild><Button size="sm" className="bg-slate-900 text-white" data-testid={`add-${kind}-btn`}><Plus size={14} className="mr-1" /> Add</Button></DialogTrigger>
            <DialogContent className={isDealers ? "max-w-2xl max-h-[90vh] overflow-y-auto" : ""}>
              <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {isDealers ? "Dealer" : ""}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${kind}-name-input`} /></div>
                {isDealers && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="dealers-phone-input" /></div>
                      <div><Label>Contact Person</Label><Input value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="dealers-contact-person-input" /></div>
                    </div>
                    <div><Label>Address</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="dealers-address-input" /></div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label>Area</Label><Input value={form.area || ""} onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="dealers-area-input" /></div>
                      <div><Label>District</Label><Input value={form.district || ""} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="dealers-district-input" /></div>
                      <div><Label>Province</Label><Input value={form.province || ""} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="dealers-province-input" /></div>
                    </div>
                    <div><Label>Email <span className="text-xs text-slate-400">(optional)</span></Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="dealers-email-input" /></div>
                  </>
                )}
                <div><Label>Remarks <span className="text-xs text-slate-400">(optional)</span></Label><Input value={form.remarks || ""} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-slate-900 text-white" data-testid={`save-${kind}-btn`}>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full dense-table">
          <thead className="bg-slate-50 border-b"><tr>
            <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Name</th>
            {isDealers && <>
              <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Contact</th>
              <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Phone</th>
              <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Address</th>
              <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Area</th>
              <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">District</th>
              <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Province</th>
              <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Status</th>
            </>}
            {!isDealers && <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Remarks</th>}
            <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} data-testid={`${kind}-row-${(it.name || "").replace(/\s+/g, "_")}`}>
                <td className="font-medium">{it.name}</td>
                {isDealers && <>
                  <td className="text-slate-700">{it.contact_person || "—"}</td>
                  <td className="font-mono text-xs text-slate-700">{it.phone || "—"}</td>
                  <td className="text-slate-700 text-xs max-w-xs truncate" title={it.address || ""}>{it.address || "—"}</td>
                  <td className="text-slate-700 text-xs">{it.area || "—"}</td>
                  <td className="text-slate-700 text-xs">{it.district || "—"}</td>
                  <td className="text-slate-700 text-xs">{it.province || "—"}</td>
                  <td className="text-xs">{it.active === false
                    ? <span className="text-amber-700">Inactive</span>
                    : <span className="text-emerald-700 font-medium">Active</span>}</td>
                </>}
                {!isDealers && <td className="text-slate-500">{it.remarks || "—"}</td>}
                <td className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(it); setForm({ ...blank, ...it }); setOpen(true); }} data-testid={`edit-${kind}-${(it.name || "").replace(/\s+/g, "_")}`}><Edit size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(it.id)}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={isDealers ? 9 : 3} className="py-12 text-center text-slate-400 text-sm">No records.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </CardContent></Card>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "ccr", phone: "" });

  const load = () => api.get("/auth/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/auth/register", form); toast.success("User created"); setOpen(false); load(); }
    catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };

  return (
    <Card><CardContent className="p-0">
      <div className="flex items-center justify-between p-3 border-b">
        <div className="text-sm text-slate-600">{users.length} users</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" className="bg-slate-900 text-white" data-testid="add-user-btn"><Plus size={14} className="mr-1" /> Add user</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
              <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-slate-900 text-white" data-testid="save-user-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <table className="w-full dense-table">
        <thead className="bg-slate-50 border-b"><tr>
          <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Name</th>
          <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Email</th>
          <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Role</th>
          <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Phone</th>
        </tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td className="font-medium">{u.name}</td>
              <td className="font-mono text-slate-700">{u.email}</td>
              <td><span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-semibold">{ROLE_LABELS[u.role] || u.role}</span></td>
              <td className="font-mono text-slate-700">{u.phone}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent></Card>
  );
}

function ProductsTab() {
  const [statusTab, setStatusTab] = useState("approved");
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { name: "", brand: "", category: "", model: "", remarks: "", active: true };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products", { params: { status: statusTab } }).then((r) => setProducts(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusTab]);
  useEffect(() => { api.get("/masters/brands").then((r) => setBrands(r.data)); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/products/${editing.id}`, form);
      else await api.post("/products", form);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(blank); load();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/products/${id}`); load(); };

  const statusBadge = (s) => {
    const v = s || "approved";
    const map = {
      approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
      pending: "bg-amber-50 text-amber-700 border-amber-200",
      needs_correction: "bg-orange-50 text-orange-700 border-orange-200",
      rejected: "bg-rose-50 text-rose-700 border-rose-200",
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${map[v] || map.approved}`} data-testid={`product-status-${v}`}>{v.replace("_", " ")}</span>;
  };

  return (
    <Card><CardContent className="p-0">
      <div className="flex items-center justify-between p-3 border-b gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="text-sm text-slate-600">{products.length} products</div>
          <div className="ml-3 flex gap-1 border rounded-md p-0.5 bg-slate-50">
            {[
              { k: "approved", label: "Approved" },
              { k: "pending", label: "Pending" },
              { k: "needs_correction", label: "Needs Correction" },
              { k: "rejected", label: "Rejected" },
              { k: "all", label: "All" },
            ].map((t) => (
              <button key={t.k}
                      onClick={() => setStatusTab(t.k)}
                      className={`px-2.5 py-1 text-xs font-medium rounded ${statusTab === t.k ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
                      data-testid={`products-status-tab-${t.k}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <ExportImportButtons kind="products" label="Products" onDone={load} />
          <ImportButton kind="products" label="Import Products (Crm Stock.xlsx)" testIdPrefix="import-products" onDone={load} />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); } }}>
            <DialogTrigger asChild><Button size="sm" className="bg-slate-900 text-white" data-testid="add-product-btn"><Plus size={14} className="mr-1" /> Add</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Product</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></div>
              <div><Label>Brand</Label>
                <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div><Label>Model</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-slate-900 text-white" data-testid="save-product-btn">Save</Button>
            </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <table className="w-full dense-table">
        <thead className="bg-slate-50 border-b"><tr>
          <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Status</th>
          <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Name</th>
          <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Brand</th>
          <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Category</th>
          <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Size</th>
          <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Model</th>
          <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Submitted by</th>
          <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Actions</th>
        </tr></thead>
        <tbody>
          {products.slice(0, 200).map((p) => (
            <tr key={p.id} data-testid={`product-row-${p.id}`}>
              <td>{statusBadge(p.status)}</td>
              <td className="font-medium">{p.name}</td>
              <td>{p.brand}</td>
              <td>{p.product_category || p.category || "—"}</td>
              <td>{p.product_size || "—"}</td>
              <td className="font-mono">{p.model}</td>
              <td className="text-xs text-slate-600">{p.created_by || "—"}</td>
              <td className="text-right">
                <Button variant="ghost" size="sm" onClick={() => { setEditing(p); setForm({ ...blank, ...p }); setOpen(true); }}><Edit size={14} /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(p.id)}><Trash2 size={14} /></Button>
              </td>
            </tr>
          ))}
          {products.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400 text-sm">No products in this state.</td></tr>}
          {products.length > 200 && <tr><td colSpan={8} className="text-center text-xs text-slate-400 py-2">Showing first 200 of {products.length}. Use Add/Edit to manage individual records.</td></tr>}
        </tbody>
      </table>
    </CardContent></Card>
  );
}

export default function Settings() {
  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage master data, users, and business catalog.</p>
      </div>
      <Tabs defaultValue="users">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="settings-tab-users">Users</TabsTrigger>
          <TabsTrigger value="products" data-testid="settings-tab-products">Products</TabsTrigger>
          {NAMED_TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`settings-tab-${t.key}`}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="products"><ProductsTab /></TabsContent>
        {NAMED_TABS.map((t) => (
          <TabsContent key={t.key} value={t.key}><NamedMaster kind={t.key} /></TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
